import { Suspense, useState } from 'react';

import { Tab } from '@headlessui/react';
import { IconDownload, IconFileText } from '@iwoca/icons';
import {
    GetCreditCardDocumentsResponse,
    GetCreditCardStatementsAccountsResponse
} from '@iwoca/lapi-client/edge';
import { Text } from '@iwoca/orion-ui';
import { format } from 'date-fns';
import { FormattedMessage } from 'react-intl';

import { ErrorBoundary } from '../../../../errorBoundary/ErrorBoundary';
import FallbackErrorModal from '../../../modals/fallbackErrorModal/FallbackErrorModal';
import MainContentPending from '../../../pageStructure/mainContentPending';
import { useFetchCreditCardStatements } from '../../hooks/useGetCreditCardStatements';
import { useFetchCreditCardStatementContent } from '../../hooks/useGetCreditCardStatementsDownload';
import { useFetchCreditCardDocumentContent } from '../../onboarding/documents/useCreditCardDocumentContent';
import { useFetchCreditCardDocumentsMetadata } from '../../onboarding/documents/useCreditCardDocumentsMetadata';

type DocumentMetadata = NonNullable<
    GetCreditCardDocumentsResponse['data']['documents']
>[number];

type Statement = NonNullable<
    GetCreditCardStatementsAccountsResponse['statements']
>[number];

const DocumentCard = ({
    title,
    onButtonClick: onDownloadButtonClick
}: {
    title: string;
    onButtonClick: () => void;
}) => {
    return (
        <div
            className="flex items-center justify-between border-b border-secondary-90 p-l"
            data-testid={`document-card-${title}`}
        >
            <div className="flex items-center">
                <IconFileText className="mr-l text-xl max-sm:text-m" />
                <Text className="font-medium max-sm:text-sm text-m">
                    {title}
                </Text>
            </div>
            <button
                onClick={onDownloadButtonClick}
                className="cursor-pointer border-0 bg-transparent p-0"
                // eslint-disable-next-line formatjs/no-literal-string-in-jsx
                aria-label="Download document"
            >
                <IconDownload className="text-xl max-sm:text-m" />
            </button>
        </div>
    );
};

const LegalDocumentCard = ({ document }: { document: DocumentMetadata }) => {
    const { refetch } = useFetchCreditCardDocumentContent({
        documentId: document.id,
        signingState: 'signed',
        fileFormat: 'pdf',
        enabled: false
    });

    return (
        <DocumentCard
            key={document.id}
            title={document.title}
            onButtonClick={() => refetch()}
        />
    );
};

const StatementCard = ({ statement }: { statement: Statement }) => {
    const { refetch } = useFetchCreditCardStatementContent({
        documentId: statement.statement_id,
        enabled: false
    });

    const formatStatementTitle = (statement: Statement) => {
        const startDate = format(
            new Date(statement.period_start),
            'dd MMM yyyy'
        );
        const endDate = format(new Date(statement.period_end), 'dd MMM yyyy');
        return `${startDate} - ${endDate}`;
    };

    return (
        <DocumentCard
            key={statement.statement_id}
            title={formatStatementTitle(statement)}
            onButtonClick={() => refetch()}
        />
    );
};

const StatementsView = () => {
    const { statements } = useFetchCreditCardStatements();

    const statementsByYear: Record<number, typeof statements> = {};
    for (const statement of statements) {
        const year = new Date(statement.period_end).getFullYear();
        if (!statementsByYear[year]) {
            statementsByYear[year] = [];
        }
        statementsByYear[year].push(statement);
    }

    const sortedYears = Object.keys(statementsByYear)
        .map(Number)
        .sort((a, b) => b - a);

    return (
        <div>
            <Text className="border-b border-secondary-90 pb-xl text-l max-sm:text-m">
                <FormattedMessage
                    id="_.credit_card.dashboard.statements_and_documents.statements_description"
                    defaultMessage="View or download your account statements below in a PDF format."
                />
            </Text>
            {statements.length === 0 && (
                <div className="mt-xl flex flex-col items-center justify-center py-3xl">
                    <IconFileText className="h-12 w-12 mb-m text-primary-40" />
                    <Text className="text-m font-med text-primary-40">
                        <FormattedMessage
                            id="_.credit_card.dashboard.statements_and_documents.no_statements"
                            defaultMessage="No statements available for download at this time."
                        />
                    </Text>
                </div>
            )}
            {sortedYears.map((year) => (
                <div key={year}>
                    <Text className="max-sm:text-sm mt-xl border-b border-secondary-90 pb-m text-m font-bold">
                        {year}
                    </Text>
                    {statementsByYear[year].map((statement) => (
                        <StatementCard
                            key={statement.statement_id}
                            statement={statement}
                        />
                    ))}
                </div>
            ))}
        </div>
    );
};

const LegalDocumentsSection = () => {
    const { documents } = useFetchCreditCardDocumentsMetadata();
    const agreedDocuments = documents?.filter((document) => document.agreed);

    if (!agreedDocuments || agreedDocuments.length === 0) {
        return (
            <div className="mt-xl flex flex-col items-center justify-center py-3xl">
                <IconFileText className="h-12 w-12 mb-m text-primary-40" />
                <Text className="text-m font-med text-primary-40">
                    <FormattedMessage
                        id="_.credit_card.dashboard.statements_and_documents.no_documents"
                        defaultMessage="No documents available"
                    />
                </Text>
            </div>
        );
    }

    return (
        <>
            {agreedDocuments.map((document) => (
                <LegalDocumentCard key={document.id} document={document} />
            ))}
        </>
    );
};

const DocumentsView = () => {
    return (
        <div>
            <Text className="m-0 border-b border-secondary-90 pb-xl text-l max-sm:text-m">
                <FormattedMessage
                    id="_.credit_card.dashboard.statements_and_documents.documents_description"
                    defaultMessage="View or download your account documents below in a PDF format."
                />
            </Text>
            <ErrorBoundary fallback={() => <FallbackErrorModal />}>
                <Suspense fallback={<MainContentPending />}>
                    <LegalDocumentsSection />
                </Suspense>
            </ErrorBoundary>
        </div>
    );
};

export const StatementsAndDocuments = () => {
    const [selectedTab, setSelectedTab] = useState<number>(1);

    const renderTabView = () => {
        if (selectedTab === 1) {
            return <StatementsView />;
        }

        if (selectedTab === 2) {
            return <DocumentsView />;
        }
    };
    return (
        <div className="flex flex-col gap-l">
            <Text className="m-0 text-2xl font-bold max-sm:text-l">
                <FormattedMessage
                    id="_.credit_card.dashboard.statements_and_documents.title"
                    defaultMessage="Statements and Documents"
                />
            </Text>

            <div className="mb-l mt-2xl rounded-m border border-secondary-80 bg-secondary-90 p-xs">
                <Tab.Group>
                    <Tab.List className="flex gap-xs">
                        <Tab
                            onClick={() => setSelectedTab(1)}
                            className="data-[headlessui-state='selected']:shadow-sm w-full rounded-m border-0 bg-transparent px-l py-m text-m font-med text-primary-40 transition-colors data-[headlessui-state='selected']:bg-structure-100 data-[headlessui-state='selected']:font-bold data-[headlessui-state='selected']:text-primary-10"
                        >
                            <FormattedMessage
                                id="_.credit_card.dashboard.statements_and_documents.statements_tab"
                                defaultMessage="Statements"
                            />
                        </Tab>
                        <Tab
                            onClick={() => setSelectedTab(2)}
                            className="data-[headlessui-state='selected']:shadow-sm w-full rounded-m border-0 bg-transparent px-l py-m text-m font-med text-primary-40 transition-colors data-[headlessui-state='selected']:bg-structure-100 data-[headlessui-state='selected']:font-bold data-[headlessui-state='selected']:text-primary-10"
                        >
                            <FormattedMessage
                                id="_.credit_card.dashboard.statements_and_documents.documents_tab"
                                defaultMessage="Documents"
                            />
                        </Tab>
                    </Tab.List>
                </Tab.Group>
            </div>
            {renderTabView()}
        </div>
    );
};
